import { config } from '../config.ts';
import type { TeacherLang } from '../types.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';
import { liveLang } from './i18nLive.ts';
import { icon } from './kit.ts';

const LABEL: Record<TeacherLang, string> = { en: 'EN', pl: 'PL' };
const NAME: Record<TeacherLang, string> = { en: 'angielski', pl: 'polski' };
/** Podpowiedź przycisku w bieżącym języku (EN: „Language: English. Tap to change.”). */
const HINT: Record<TeacherLang, string> = { en: 'Language: English. Tap to change.', pl: 'Język: polski. Dotknij, aby zmienić.' };
/** Nazwy języków na liście (zawsze we własnym języku). */
const OWN: Record<TeacherLang, string> = { en: 'English', pl: 'Polski' };

/**
 * „EN ⌄” w pasku lekcji: rozwijana lista z samymi kodami „EN” i „PL” — język aplikacji: napisy na ekranach (przyciski, nagłówki, komunikaty)
 * i linijki bota (pogadanki, pożegnanie). Zmiana działa od razu: napisy się przepisują, a bieżąca linijka bota jest czytana jeszcze raz w nowym języku.
 * Lista zamyka się po wyborze, po dotknięciu poza nią i klawiszem Esc.
 */
export function renderLangToggle(ctx: AppCtx): HTMLElement | null {
  const t = ctx.teacher;
  if (!t) return null;
  const doc = document as unknown as { addEventListener?(type: string, fn: (e: Event) => void): void; removeEventListener?(type: string, fn: (e: Event) => void): void };
  let open = false;
  const label = h('span', { class: 'lang-label' });
  const btn = h('button', { type: 'button', class: 'lang-btn', 'data-testid': 'lang-toggle', 'aria-haspopup': 'true', 'aria-expanded': 'false', onclick: () => setOpen(!open) }, label, icon('down'));
  const menu = h('div', { class: 'lang-menu', role: 'menu', 'data-testid': 'lang-menu', hidden: true });
  const root = h('div', { class: 'lang', 'data-keeps-help': '1' }, btn, menu); // zmiana języka nie zamyka karty „jak czytać” (zob. dismissOnOutsideTap)

  const onOutside = (e: Event): void => {
    const target = e.target as Node | null;
    if ((root as { isConnected?: boolean }).isConnected === false) return setOpen(false);
    if (target && root.contains(target)) return;
    setOpen(false);
  };
  const onKey = (e: Event): void => {
    if ((e as KeyboardEvent).key === 'Escape') setOpen(false);
  };
  function setOpen(v: boolean): void {
    if (v === open) return;
    open = v;
    menu.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    if (open) {
      doc.addEventListener?.('click', onOutside);
      doc.addEventListener?.('keydown', onKey);
    } else {
      doc.removeEventListener?.('click', onOutside);
      doc.removeEventListener?.('keydown', onKey);
    }
  }

  const paint = (): void => {
    const lang = t.lang();
    label.textContent = LABEL[lang];
    const hint = HINT[lang];
    btn.setAttribute('title', hint);
    btn.setAttribute('aria-label', hint);
    btn.setAttribute('data-lang', lang);
    menu.replaceChildren(
      ...(['en', 'pl'] as const).map((l) =>
        h('button', {
          type: 'button',
          class: `lang-opt${l === lang ? ' on' : ''}`,
          role: 'menuitemradio',
          'aria-checked': String(l === lang),
          'data-testid': `lang-opt-${l}`,
          onclick: () => {
            setOpen(false);
            t.set(l);
            paint();
          },
          'aria-label': OWN[l],
          title: OWN[l],
        }, LABEL[l]),
      ),
    );
  };
  paint();
  liveLang(ctx, root, paint); // język zmieniony gdzie indziej (np. w „Dla rodzica”) też przepisuje podpowiedź
  return root;
}

const UI_NOTE = 'Ten wybór dotyczy też wszystkich napisów w aplikacji dla dziecka (przyciski, nagłówki, komunikaty): po angielsku albo po polsku. Panel rodzica jest zawsze po polsku.';

/** „Dla rodzica”: wybór języka aplikacji — linijek bota w lekcji (pogadanki i pożegnanie) oraz napisów na ekranach (to samo, co „EN ⌄” w pasku lekcji). Panel rodzica zostaje po polsku. */
export function renderLangPanel(ctx: AppCtx): HTMLElement {
  const t = ctx.teacher;
  if (!t) return h('div', { hidden: true });
  const note = h('p', { class: 'muted', 'data-testid': 'lang-note' });
  const buttons = (['en', 'pl'] as const).map((lang) => button(lang === 'en' ? 'English' : 'Polski', () => {
    t.set(lang);
    refresh();
  }, { class: 'btn ghost', testid: `lang-${lang}` }));
  const refresh = () => {
    const cur = t.lang();
    buttons.forEach((b, i) => {
      b.className = `btn ghost${(['en', 'pl'] as const)[i] === cur ? ' on' : ''}`;
      b.setAttribute('aria-pressed', String((['en', 'pl'] as const)[i] === cur));
    });
    note.textContent = cur === 'en'
      ? 'Bot mówi swoje linijki (pogadanki, pożegnanie) po angielsku, a polskie tłumaczenie jest pod przyciskiem „Po polsku” (w angielskim interfejsie: „In Polish”). Domyślnie: ' + NAME[config.teacher.defaultLang] + '. ' + UI_NOTE
      : 'Bot mówi swoje linijki (pogadanki, pożegnanie) po polsku; słowa i zdania do powtórzenia nadal po angielsku. Uwagi pod oceną wymowy są zawsze po polsku. ' + UI_NOTE;
  };
  refresh();
  return h('div', { class: 'diag-box', 'data-testid': 'lang-panel' }, h('p', null, '🗣 Język aplikacji i linijek bota'), h('div', { class: 'row' }, ...buttons), note);
}

/** Przełącznik języka w rogu ekranu bez paska (powitanie, logowanie, wybór profilu); `main` dostaje klasę, która rezerwuje mu miejsce (zob. koniec styles.css). */
export function renderLangCorner(ctx: AppCtx): HTMLElement | null {
  const toggle = renderLangToggle(ctx);
  return toggle ? h('div', { class: 'lang-corner', 'data-testid': 'lang-corner' }, toggle) : null;
}
